import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  getRecallClientReceipts,
  getRecallEntries,
  getRecallEntry,
  postGenerateRecallDocument,
  postPrintRecallAwb,
  postRequestRecallAwb,
  postReturnRecallDocument,
  postScanRecallDocument,
  postSendRecallEmail,
} from "../api/recall.api";
import {
  RecallAwbPrintInterface,
  RecallClientReceiptsInterface,
  RecallEntriesInterface,
  RecallClientBatchSelectionInterface,
  RecallEntryDetailsInterface,
  RecallScanInterface,
} from "../interfaces/recall.interface";

export interface StateRecallInterface {
  entries: RecallEntriesInterface;
  entry: RecallEntryDetailsInterface;
  scan: RecallScanInterface;
  clientReceipts: RecallClientReceiptsInterface;
  awbPrint: RecallAwbPrintInterface;
  entryRequestId: string | null;
  scanRequestId: string | null;
  clientReceiptsRequestId: string | null;
  awbPrintRequestId: string | null;
  clientBatch: {
    data: RecallClientBatchSelectionInterface | null;
    isLoading: boolean;
  };
  pendingClientActionIds: string[];
  awbAttemptedClientIds: number[];
}

export const defaultStateRecall: StateRecallInterface = {
  entries: {
    data: {
      data: [],
      total: 0,
      current_page: 1,
    },
    isLoading: false,
    isError: false,
    errorMessage: "",
  },
  entry: {
    data: null,
    isLoading: true,
    isError: false,
    errorMessage: "",
  },
  scan: {
    data: null,
    isLoading: false,
    isError: false,
    errorMessage: "",
  },
  clientReceipts: {
    data: null,
    isLoading: false,
    isError: false,
    errorMessage: "",
  },
  awbPrint: {
    data: null,
    isLoading: false,
    isError: false,
    errorMessage: "",
  },
  entryRequestId: null,
  scanRequestId: null,
  clientReceiptsRequestId: null,
  awbPrintRequestId: null,
  clientBatch: { data: null, isLoading: false },
  pendingClientActionIds: [],
  awbAttemptedClientIds: [],
};

const recallReducer = createSlice({
  name: "recall",
  initialState: defaultStateRecall,
  reducers: {
    clearRecallEntry: (state) => {
      return {
        ...state,
        entry: defaultStateRecall.entry,
        entryRequestId: null,
        clientBatch: defaultStateRecall.clientBatch,
        pendingClientActionIds: [],
        awbAttemptedClientIds: [],
      };
    },
    clearRecallScan: (state) => {
      return {
        ...state,
        scan: defaultStateRecall.scan,
        scanRequestId: null,
      };
    },
    clearRecallClientReceipts: (state) => {
      state.clientReceipts = defaultStateRecall.clientReceipts;
      state.clientReceiptsRequestId = null;
    },
    clearRecallAwbPrint: (state) => {
      if (state.awbPrint.isLoading) return;
      state.awbPrint = defaultStateRecall.awbPrint;
      state.awbPrintRequestId = null;
    },
    openRecallClientBatch: (
      state,
      action: PayloadAction<RecallClientBatchSelectionInterface>,
    ) => {
      state.clientBatch = { data: action.payload, isLoading: false };
    },
    closeRecallClientBatch: (state) => {
      state.clientBatch = defaultStateRecall.clientBatch;
    },
    setRecallClientBatchLoading: (state, action: PayloadAction<boolean>) => {
      state.clientBatch.isLoading = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(postPrintRecallAwb.pending, (state, action) => {
      state.awbPrint = { ...defaultStateRecall.awbPrint, isLoading: true };
      state.awbPrintRequestId = action.meta.requestId;
    });
    builder.addCase(postPrintRecallAwb.fulfilled, (state, action) => {
      if (state.awbPrintRequestId !== action.meta.requestId) return;
      state.awbPrint = {
        ...defaultStateRecall.awbPrint,
        data: action.payload,
      };
    });
    builder.addCase(postPrintRecallAwb.rejected, (state, action: any) => {
      if (state.awbPrintRequestId !== action.meta.requestId) return;
      state.awbPrint = {
        ...defaultStateRecall.awbPrint,
        isError: true,
        errorMessage: action.payload?.message ?? "",
      };
    });
    builder.addCase(getRecallClientReceipts.pending, (state, action) => {
      state.clientReceipts = {
        ...defaultStateRecall.clientReceipts,
        isLoading: true,
      };
      state.clientReceiptsRequestId = action.meta.requestId;
    });
    builder.addCase(getRecallClientReceipts.fulfilled, (state, action) => {
      if (state.clientReceiptsRequestId !== action.meta.requestId) return;
      state.clientReceipts = {
        ...defaultStateRecall.clientReceipts,
        data: action.payload,
      };
    });
    builder.addCase(getRecallClientReceipts.rejected, (state, action: any) => {
      if (state.clientReceiptsRequestId !== action.meta.requestId) return;
      state.clientReceipts = {
        ...defaultStateRecall.clientReceipts,
        isError: true,
        errorMessage: action.payload?.message ?? "",
      };
    });
    builder.addCase(getRecallEntries.pending, (state) => {
      return {
        ...state,
        entries: {
          ...defaultStateRecall.entries,
          isLoading: true,
        },
      };
    });
    builder.addCase(getRecallEntries.rejected, (state, action: any) => {
      return {
        ...state,
        entries: {
          ...state.entries,
          isLoading: false,
          isError: true,
          errorMessage: action.payload?.message ?? "",
        },
      };
    });
    builder.addCase(getRecallEntries.fulfilled, (state, action) => {
      return {
        ...state,
        entries: {
          ...state.entries,
          isLoading: false,
          data: action.payload,
        },
      };
    });
    builder.addCase(getRecallEntry.pending, (state, action) => {
      const isSameCampaign =
        state.entry.data?.idCampaign === Number(action.meta.arg.id);
      return {
        ...state,
        entry: {
          ...defaultStateRecall.entry,
          data: isSameCampaign ? state.entry.data : null,
          isLoading: true,
        },
        entryRequestId: action.meta.requestId,
        awbAttemptedClientIds: isSameCampaign
          ? state.awbAttemptedClientIds
          : [],
      };
    });
    builder.addCase(getRecallEntry.rejected, (state, action: any) => {
      if (state.entryRequestId !== action.meta.requestId) return state;
      return {
        ...state,
        entry: {
          ...state.entry,
          isLoading: false,
          isError: true,
          errorMessage: action.payload?.message ?? "",
        },
      };
    });
    builder.addCase(getRecallEntry.fulfilled, (state, action) => {
      if (state.entryRequestId !== action.meta.requestId) return state;
      return {
        ...state,
        entry: {
          ...defaultStateRecall.entry,
          isLoading: false,
          data: action.payload,
        },
      };
    });
    builder.addCase(postGenerateRecallDocument.fulfilled, (state, action) => {
      state.pendingClientActionIds = state.pendingClientActionIds.filter(
        (id) => id !== action.meta.requestId,
      );
      const entry = state.entry.data;
      if (entry?.idCampaign !== action.payload.idCampaign) return;
      entry.documents = entry.documents.filter(
        (document) => document.clientPlId !== action.payload.clientPlId,
      );
      entry.documents.push(action.payload);
    });
    builder.addCase(postGenerateRecallDocument.pending, (state, action) => {
      state.pendingClientActionIds.push(action.meta.requestId);
    });
    builder.addCase(postGenerateRecallDocument.rejected, (state, action) => {
      state.pendingClientActionIds = state.pendingClientActionIds.filter(
        (id) => id !== action.meta.requestId,
      );
    });
    builder.addCase(postSendRecallEmail.pending, (state, action) => {
      state.pendingClientActionIds.push(action.meta.requestId);
    });
    builder.addCase(postSendRecallEmail.fulfilled, (state, action) => {
      state.pendingClientActionIds = state.pendingClientActionIds.filter(
        (id) => id !== action.meta.requestId,
      );
      const entry = state.entry.data;
      if (entry?.idCampaign !== action.payload.idCampaign) return;
      entry.emails = entry.emails.filter(
        (email) => email.clientPlId !== action.payload.clientPlId,
      );
      entry.emails.push(action.payload);
    });
    builder.addCase(postSendRecallEmail.rejected, (state, action) => {
      state.pendingClientActionIds = state.pendingClientActionIds.filter(
        (id) => id !== action.meta.requestId,
      );
    });
    builder.addCase(postRequestRecallAwb.fulfilled, (state, action) => {
      state.pendingClientActionIds = state.pendingClientActionIds.filter(
        (id) => id !== action.meta.requestId,
      );
      const entry = state.entry.data;
      if (entry?.idCampaign !== action.payload.idCampaign) return;
      entry.awbs = entry.awbs.filter(
        (awb) => awb.clientPlId !== action.payload.clientPlId,
      );
      entry.awbs.push(action.payload);
    });
    builder.addCase(postRequestRecallAwb.pending, (state, action) => {
      state.pendingClientActionIds.push(action.meta.requestId);
      if (!state.awbAttemptedClientIds.includes(action.meta.arg.clientPlId)) {
        state.awbAttemptedClientIds.push(action.meta.arg.clientPlId);
      }
    });
    builder.addCase(postRequestRecallAwb.rejected, (state, action) => {
      state.pendingClientActionIds = state.pendingClientActionIds.filter(
        (id) => id !== action.meta.requestId,
      );
    });
    builder.addCase(postScanRecallDocument.pending, (state, action) => {
      state.scan = { ...defaultStateRecall.scan, isLoading: true };
      state.scanRequestId = action.meta.requestId;
    });
    builder.addCase(postScanRecallDocument.fulfilled, (state, action) => {
      if (state.scanRequestId !== action.meta.requestId) return;
      state.scan = { ...defaultStateRecall.scan, data: action.payload };
    });
    builder.addCase(postScanRecallDocument.rejected, (state, action: any) => {
      if (state.scanRequestId !== action.meta.requestId) return;
      state.scan = {
        ...defaultStateRecall.scan,
        isError: true,
        errorMessage: action.payload?.message ?? "",
      };
    });
    builder.addCase(postReturnRecallDocument.pending, (state, action) => {
      state.scan.isLoading = true;
      state.scan.isError = false;
      state.scan.errorMessage = "";
      state.scanRequestId = action.meta.requestId;
    });
    builder.addCase(postReturnRecallDocument.fulfilled, (state, action) => {
      if (state.scanRequestId !== action.meta.requestId) return;
      state.scan.isLoading = false;
      if (state.scan.data?.barcodeToken === action.payload.barcodeToken) {
        state.scan.data = { ...state.scan.data, ...action.payload };
      }
    });
    builder.addCase(postReturnRecallDocument.rejected, (state, action: any) => {
      if (state.scanRequestId !== action.meta.requestId) return;
      state.scan.isLoading = false;
      state.scan.isError = true;
      state.scan.errorMessage = action.payload?.message ?? "";
    });
  },
});

export const {
  clearRecallAwbPrint,
  clearRecallClientReceipts,
  clearRecallEntry,
  clearRecallScan,
  openRecallClientBatch,
  closeRecallClientBatch,
  setRecallClientBatchLoading,
} = recallReducer.actions;

export default recallReducer.reducer;
